import React, { Component, ReactNode } from 'react';

/**
 * ErrorBoundary -- V2 canonical shared error boundary.
 *
 * Captures render errors in a React subtree. Typical use: wrap heavy or
 * external components (Google Maps, charts) and surface tree (Phase 3.5.1
 * Plan 19 main.tsx 3-chunk lazy split) so a single failure does not crash
 * the rest of the page.
 *
 * Phase 3.5.1 Plan 04 Task 2 enrichment (2026-05-16):
 *   1. `fallback` prop is now OPTIONAL. When omitted, the boundary renders
 *      the internal <DefaultFallback /> Card (hardcoded EN copy, since i18n
 *      may have failed too).
 *   2. New optional `onCaughtError?: (error, info) => void` prop. Lets the
 *      caller route the captured error to an observability sink (e.g. a
 *      future Sentry wrapper injected by main.tsx). The boundary itself
 *      stays SDK-free — no @sentry/react import. Owner decision 2026-05-16
 *      (Option 1, callback pattern) per Phase 02 D-B2 LOCKED which defers
 *      web Sentry to Phase 4+.
 *   3. In dev (`import.meta.env.DEV`) the boundary ALSO console.errors
 *      regardless of whether `onCaughtError` was passed -- preserves the
 *      existing dev-loop signal. Production: silent unless callback wired.
 *
 * React does not support error boundaries as function components -- this
 * MUST stay a class component.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback element. If omitted, <DefaultFallback /> renders. */
  fallback?: ReactNode;
  /**
   * Optional sink for caught errors. Called from componentDidCatch.
   * Plan 19 main.tsx passes `undefined`; Phase 4+ wraps and injects a
   * Sentry wrapper without changing this contract.
   */
  onCaughtError?: (error: Error, info: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

function DefaultFallback(): React.ReactElement {
  // Card pattern per UI-SPEC 03.5.1: bg-gray-50 body + bg-white card +
  // rounded-[2rem] + max-w-md. Hardcoded EN copy -- i18n may have failed too.
  return (
    <div className="bg-gray-50 min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-[2rem] p-8 max-w-md w-full border border-gray-200 shadow-sm">
        <p className="text-sm text-gray-700 text-center">
          Something went wrong. Please reload the page.
        </p>
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Owner-injected sink (Phase 4+ Sentry wrapper, etc).
    this.props.onCaughtError?.(error, info);

    // Default dev-only signal. Importing.meta.env.DEV is Vite-injected;
    // safe to read at runtime in both web and test (happy-dom) envs.
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultFallback />;
    }
    return this.props.children;
  }
}
